import React, { useState, useEffect } from "react";

interface IdleTimeoutModalProps {
  /** When true, modal is visible. */
  open: boolean;
  /** Remaining milliseconds until auto-logout (used for countdown display). */
  remainingMs: number;
  /** Called when user clicks "Stay logged in" — resets idle timer and closes modal. */
  onStayLoggedIn: () => void;
}

/**
 * Modal shown at T-5 minutes before idle logout.
 * NCSC-recommended control for OFFICIAL-SENSITIVE: warn user, then auto-logout at T-0.
 */
export const IdleTimeoutModal: React.FC<IdleTimeoutModalProps> = ({
  open,
  remainingMs,
  onStayLoggedIn,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(
    Math.max(0, Math.ceil(remainingMs / 1000))
  );

  useEffect(() => {
    if (!open || remainingMs <= 0) return;
    setSecondsLeft(Math.max(0, Math.ceil(remainingMs / 1000)));
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [open, remainingMs]);

  if (!open) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const countdown = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-timeout-title"
      aria-describedby="idle-timeout-desc"
    >
      <div
        className="mx-4 w-full max-w-md rounded-[18px] border border-[#E761A0]/70 bg-[#07040F] sm:p-8 p-6 shadow-[0_0_30px_rgba(231,97,160,0.22)] ring-1 ring-[#E761A0]/20"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2
          id="idle-timeout-title"
          className="text-lg font-semibold text-[#E761A0] mb-2 tracking-tight"
        >
          Session timeout warning
        </h2>
        <p id="idle-timeout-desc" className="text-white/85 text-sm font-light leading-relaxed mb-6">
          You will be logged out in{" "}
          <strong className="font-semibold text-[#E761A0] tabular-nums">
            {countdown}
          </strong>{" "}
          due to inactivity. Click &quot;Stay logged in&quot; to continue.
        </p>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onStayLoggedIn}
            className="min-h-[48px] rounded-[27px] border-2 border-[#E761A0] bg-[#E761A0] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-transparent hover:text-[#E761A0]"
          >
            Stay logged in
          </button>
        </div>
      </div>
    </div>
  );
};
